import type { ReminderSchedule, Tag, Track, ValidationError } from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { IconButton } from '../../components/Button.js'
import {
  ActionBar,
  ActionButton,
  Page,
  SubHeader,
  Swatch,
} from '../../components/Layout.js'
import { checkInColour } from '../../charts/colours.js'
import { Chip } from '../../components/Chip.js'
import { TextArea, TextField } from '../../components/Field.js'
import { useToast } from '../../components/Toast.js'
import { UnsavedWorkGuard, useDiscardGuard } from '../../shell/DiscardGuard.js'
import { useServices } from '../../services/ServicesProvider.js'
import { DEFAULT_WHEN, WhenToAsk, type WhenToAskState } from './WhenToAsk.js'
import styles from './Templates.module.css'

/**
 * Building a check-in (T-303).
 *
 * Deliberately without conditional logic, branching, or required-versus-optional
 * rules. A template is a set of tracks in an order; every rule added here is a
 * decision the user has to make at 1pm with food going cold.
 */
export function TemplateEditorScreen() {
  const { repos } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()
  const params = useParams()
  const templateId = params['id']
  const isNew = templateId === undefined || templateId === 'new'

  const [loaded, setLoaded] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [chosen, setChosen] = useState<string[]>([])
  const [tagIds, setTagIds] = useState<string[]>([])
  const [tracks, setTracks] = useState<Track[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [dirty, setDirty] = useState(false)

  /*
   * The schedule this check-in already has, if any.
   *
   * One per template here, which is what a section inside an editor implies.
   * The model allows several and allows one with no template at all — those
   * still live on `/reminders`, which keeps its own list and its own editor.
   * Narrowing the model to match this section would have removed working
   * capability to fit a drawing.
   */
  const [schedule, setSchedule] = useState<ReminderSchedule | null>(null)
  const [when, setWhen] = useState<WhenToAskState>(DEFAULT_WHEN)

  const guard = useDiscardGuard(dirty)

  const load = useCallback(async () => {
    const [allTracks, allTags] = await Promise.all([
      repos.tracks.list({ includeArchived: true }),
      repos.tags.list(),
    ])
    setTracks(allTracks)
    setTags(allTags)

    if (!isNew) {
      const template = await repos.templates.get(templateId)
      if (template) {
        setName(template.name)
        setDescription(template.description ?? '')
        setChosen([...template.trackIds])
        setTagIds([...template.defaultTagIds])
      }
      const existing = (await repos.reminders.list()).find(
        (reminder) => reminder.templateId === templateId,
      )
      if (existing !== undefined) {
        setSchedule(existing)
        setWhen({ enabled: existing.enabled, recurrence: existing.recurrence })
      }
    }
    setLoaded(true)
  }, [isNew, templateId, repos])

  useEffect(() => {
    void load()
  }, [load])

  const byId = new Map(tracks.map((track) => [track.id, track]))
  const available = tracks.filter(
    (track) => !track.archived && !chosen.includes(track.id),
  )

  function move(index: number, to: number) {
    if (to < 0 || to >= chosen.length) {
      return
    }
    const next = [...chosen]
    const [moved] = next.splice(index, 1)
    if (moved === undefined) {
      return
    }
    next.splice(to, 0, moved)
    setChosen(next)
    setDirty(true)
  }

  /*
   * Writes, and reports whether it worked — without navigating. See the same
   * split in the track editor: the unsaved-changes sheet is already holding a
   * navigation, and a failed save must not complete it.
   */
  async function persist(): Promise<boolean> {
    setErrors([])
    const input = {
      name: name.trim(),
      trackIds: chosen,
      defaultTagIds: tagIds,
      ...(description.trim() === '' ? {} : { description: description.trim() }),
    }

    const result = isNew
      ? await repos.templates.create(input)
      : await repos.templates.update(templateId, input)

    if (!result.ok) {
      setErrors(result.errors)
      return false
    }

    /*
     * The schedule is saved with the check-in, because it is part of the same
     * object as far as the user is concerned. A failure here reports and stops
     * rather than leaving the two out of step silently.
     */
    const scheduleResult = await saveSchedule(result.value.id, result.value.name)
    if (scheduleResult !== null) {
      setErrors(scheduleResult)
      return false
    }

    setDirty(false)
    guard.allowLeaving()
    show({ message: isNew ? `Created ${result.value.name}.` : 'Saved.' })
    return true
  }

  async function save() {
    if (await persist()) {
      void navigate('/templates')
    }
  }

  /**
   * Writes the schedule, and returns any validation errors rather than throwing.
   *
   * Turning the section off keeps the schedule and disables it — a paused
   * reminder that had to be rebuilt from scratch is not a pause.
   */
  async function saveSchedule(
    id: string,
    templateName: string,
  ): Promise<readonly ValidationError[] | null> {
    if (schedule === null && !when.enabled) {
      // Never configured and still not wanted. Nothing to store.
      return null
    }

    const result =
      schedule === null
        ? await repos.reminders.create({
            name: templateName,
            templateId: id,
            recurrence: when.recurrence,
          })
        : await repos.reminders.update(schedule.id, {
            recurrence: when.recurrence,
            enabled: when.enabled,
          })

    if (!result.ok) {
      return result.errors
    }
    /*
     * A schedule created here starts enabled, because the only way to reach
     * this branch is by switching the section on.
     */
    return null
  }

  const back = { to: '/templates', label: 'Check-ins' }

  if (!loaded) {
    return (
      <Page>
        <SubHeader back={back} />
        <h1>Check-in</h1>
        <p>Loading…</p>
      </Page>
    )
  }

  return (
    <Page>
      <SubHeader back={back} />
      <div className={styles['title']}>
        {templateId !== undefined && !isNew && (
          <Swatch colour={checkInColour({ id: templateId })} size={14} />
        )}
        <h1>{isNew ? 'New check-in' : name || 'Check-in'}</h1>
      </div>
      <UnsavedWorkGuard
        guard={guard}
        what={isNew ? 'This new check-in' : 'This check-in'}
        onSave={persist}
      />

      <div className={styles['form']} data-bleed>
        <section className={styles['section']} data-bleed>
          <TextField
            label="Name"
            value={name}
            placeholder="Before eating, Bedtime…"
            onChange={(event) => {
              setName(event.target.value)
              setDirty(true)
            }}
          />
          <TextArea
            label="Description"
            optional
            value={description}
            onChange={(event) => {
              setDescription(event.target.value)
              setDirty(true)
            }}
          />
        </section>

        <section className={styles['section']} data-bleed>
          <h2 className={styles['sectionTitle']}>
            Tracks, in the order you will see them
          </h2>

          {chosen.length === 0 ? (
            <p className={styles['empty']}>
              {/*
                The consequence of the next action, not the name of the control.
                It is the only place the order-matters rule is stated at the
                moment somebody is about to set the order.
              */}
              Nothing chosen yet. Add a track below and it becomes the first question.
            </p>
          ) : (
            <div className={styles['chosen']}>
              {chosen.map((trackId, index) => {
                const track = byId.get(trackId)
                // Archived or deleted since this template was written. Flagged
                // in place with a one-tap fix rather than a hard error — the
                // user should still be able to log the rest today.
                const stale = track === undefined || track.archived
                return (
                  <div
                    key={trackId}
                    className={`${styles['chosenTrack']} ${stale ? styles['stale'] : ''}`}
                  >
                    <span>
                      <span>{track?.name ?? 'A deleted track'}</span>
                      {stale && (
                        <span className={styles['staleNote']}>
                          {track === undefined
                            ? ' — this track no longer exists'
                            : ' — this track is archived, so it will not be asked for'}
                        </span>
                      )}
                    </span>
                    <span className={styles['actions']}>
                      <IconButton
                        label={`Move ${track?.name ?? 'track'} up`}
                        disabled={index === 0}
                        onClick={() => move(index, index - 1)}
                      >
                        <Chevron up />
                      </IconButton>
                      <IconButton
                        label={`Move ${track?.name ?? 'track'} down`}
                        disabled={index === chosen.length - 1}
                        onClick={() => move(index, index + 1)}
                      >
                        <Chevron />
                      </IconButton>
                      <IconButton
                        label={`Remove ${track?.name ?? 'this track'} from the check-in`}
                        onClick={() => {
                          setChosen(chosen.filter((id) => id !== trackId))
                          setDirty(true)
                        }}
                      >
                        <Cross />
                      </IconButton>
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          {available.length > 0 && (
            <div className={styles['available']}>
              {available.map((track) => (
                <Chip
                  key={track.id}
                  onClick={() => {
                    setChosen([...chosen, track.id])
                    setDirty(true)
                  }}
                >
                  + {track.name}
                </Chip>
              ))}
            </div>
          )}
        </section>

        <WhenToAsk
          value={when}
          onChange={(next) => {
            setWhen(next)
            setDirty(true)
          }}
        />

        <section className={styles['section']} data-bleed>
          <h2 className={styles['sectionTitle']}>Tags applied to every entry</h2>
          {tags.length === 0 ? (
            <p className={styles['empty']}>
              No tags yet. You can create them while logging.
            </p>
          ) : (
            <div className={styles['available']}>
              {tags.map((tag) => (
                <Chip
                  key={tag.id}
                  {...(tag.colour === undefined ? {} : { colour: tag.colour })}
                  selected={tagIds.includes(tag.id)}
                  onClick={() => {
                    setTagIds(
                      tagIds.includes(tag.id)
                        ? tagIds.filter((id) => id !== tag.id)
                        : [...tagIds, tag.id],
                    )
                    setDirty(true)
                  }}
                >
                  {tag.name}
                </Chip>
              ))}
            </div>
          )}
        </section>

        {errors.length > 0 && (
          <div className={styles['errors']} role="alert">
            {errors.map((error) => (
              <span key={error.code} className={styles['error']}>
                {error.message}
              </span>
            ))}
          </div>
        )}
      </div>

      <ActionBar>
        <ActionButton onClick={() => void navigate('/templates')}>Cancel</ActionButton>
        <ActionButton primary onClick={() => void save()}>
          {isNew ? 'Create check-in' : 'Save'}
        </ActionButton>
      </ActionBar>
    </Page>
  )
}

function Chevron({ up = false }: { readonly up?: boolean }) {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d={up ? 'M5 12l5-5 5 5' : 'M5 8l5 5 5-5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Cross() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M5 5l10 10M15 5L5 15"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}
